import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, Camera, Zap, Clock, TrendingUp, Check, Menu, X, FileText, DollarSign, User, Wrench, Users, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SignedIn, SignedOut, UserButton, useUser } from '@clerk/clerk-react';
import { createPortalSession } from './utils/subscriptionClient';

function AnimatedEstimateCard() {
  const PHRASE = '"Kitchen remodel, 200 sq ft \u2014 new shaker cabinets, quartz counters, subway tile backsplash, LVP flooring."';
  const TOTAL = 24850;
  const LINE_ITEMS = [
    { label: 'Base Cabinets, Shaker', value: '$5,100' },
    { label: 'Quartz Countertop, 45 sq ft', value: '$3,375' },
    { label: 'LVP Flooring, 200 sq ft', value: '$1,500' },
    { label: 'Labor + markup\u2026', value: '+39 more', muted: true },
  ];
  const fmt = new Intl.NumberFormat('en-US');
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const [typed, setTyped] = useState(PHRASE);
  const [cursorOn, setCursorOn] = useState(false);
  const [aiVisible, setAiVisible] = useState(true);
  const [aiText, setAiText] = useState('Estimate ready for review');
  const [metaVisible, setMetaVisible] = useState(true);
  const [displayTotal, setDisplayTotal] = useState(TOTAL);
  const [linesShown, setLinesShown] = useState(LINE_ITEMS.length);
  const [btnVisible, setBtnVisible] = useState(true);

  const rafRef = useRef(null);
  const runningRef = useRef(false);
  const containerRef = useRef(null);

  const countTo = useCallback((target, dur) => {
    return new Promise((resolve) => {
      let start = null;
      const step = (ts) => {
        if (start === null) start = ts;
        const p = Math.min((ts - start) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplayTotal(Math.round(target * eased));
        if (p < 1) { rafRef.current = requestAnimationFrame(step); } else { resolve(); }
      };
      rafRef.current = requestAnimationFrame(step);
    });
  }, []);

  const runSequence = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setTyped(''); setCursorOn(true); setAiVisible(false);
    setAiText('Building your estimate\u2026'); setMetaVisible(false);
    setDisplayTotal(0); setLinesShown(0); setBtnVisible(false);
    await sleep(500);
    for (let i = 0; i < PHRASE.length; i++) {
      setTyped(PHRASE.slice(0, i + 1));
      await sleep(PHRASE[i] === ' ' ? 22 : 30);
    }
    await sleep(450); setCursorOn(false); setAiVisible(true);
    await sleep(1300);
    setAiText('Your estimate is ready to review'); setMetaVisible(true);
    await countTo(TOTAL, 1100);
    for (let i = 0; i < LINE_ITEMS.length; i++) { setLinesShown(i + 1); await sleep(220); }
    await sleep(300); setBtnVisible(true);
    runningRef.current = false;
  }, [countTo]);

  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const node = containerRef.current;
    let observer;
    if (node && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting && !runningRef.current) runSequence(); });
      }, { threshold: 0.4 });
      observer.observe(node);
    } else {
      runSequence();
    }
    return () => {
      if (observer) observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      runningRef.current = false;
    };
  }, [runSequence]);

  return (
    <div className="relative" ref={containerRef}>
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 tracking-wide mb-2">
            <Mic size={14} className="text-blue-600" /> YOU SAID
          </div>
          <div className="text-sm text-gray-800 italic leading-relaxed" style={{ minHeight: '60px' }}>
            {typed}
            <span className="inline-block align-middle ml-0.5" style={{ width: '2px', height: '16px', background: '#2563EB', opacity: cursorOn ? 1 : 0 }}></span>
          </div>
        </div>
        <div className="px-6 py-2.5 bg-blue-50 flex items-center justify-center gap-2" style={{ opacity: aiVisible ? 1 : 0, transition: 'opacity 0.4s ease' }}>
          <Zap size={14} className="text-blue-600" />
          <span className="text-xs font-semibold text-blue-600">{aiText}</span>
        </div>
        <div className="px-6 pt-4 pb-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 tracking-wide">SAMPLE ESTIMATE</p>
              <p className="text-3xl font-bold text-gray-900 mt-0.5">${fmt.format(displayTotal)}</p>
            </div>
            <div className="text-right text-xs text-gray-500 leading-snug" style={{ opacity: metaVisible ? 1 : 0, transition: 'opacity 0.4s ease' }}>
              42 line items<br />across 5 categories
            </div>
          </div>
          <div className="text-sm">
            {LINE_ITEMS.map((item, i) => (
              <div
                key={i}
                className={'flex justify-between py-1.5 ' + (i < LINE_ITEMS.length - 1 ? 'border-b border-gray-100' : '')}
                style={{ opacity: i < linesShown ? 1 : 0, transform: i < linesShown ? 'translateY(0)' : 'translateY(6px)', transition: 'opacity 0.35s ease, transform 0.35s ease' }}
              >
                <span className={item.muted ? 'text-gray-400' : 'text-gray-700'}>{item.label}</span>
                <span className={item.muted ? 'text-gray-400' : 'text-gray-900 font-semibold'}>{item.value}</span>
              </div>
            ))}
          </div>
          <button
            className="w-full bg-gradient-to-r from-gray-700 to-gray-800 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition mt-4 flex items-center justify-center gap-2"
            style={{ opacity: btnVisible ? 1 : 0, transition: 'opacity 0.4s ease' }}
          >
            <FileText size={15} /> Export Professional PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function Homepage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useUser();

  const handleManageSubscription = async () => {
    try {
      await createPortalSession(user);
    } catch (error) {
      console.error('Error opening customer portal:', error);
      alert('Failed to open subscription management. Please try again.');
    }
  };

  return (
    <div className="bg-white">
      {/* Navigation */}
      <nav className="fixed w-full top-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img src="/Housecalclogo.png" alt="Estimate Assistant - Contractor Estimating Software" className="h-12" />
              <span className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Estimate Assistant
              </span>
            </div>
            
           {/* Desktop Navigation */}
           <div className="hidden md:flex items-center space-x-8">
             <a href="#features" className="text-gray-700 hover:text-blue-600 transition font-medium">Features</a>
             <a href="#how-it-works" className="text-gray-700 hover:text-blue-600 transition font-medium">How It Works</a>
             <a href="#faq" className="text-gray-700 hover:text-blue-600 transition font-medium">FAQ</a>
             <Link to="/pricing" className="text-gray-700 hover:text-blue-600 transition font-medium">Pricing</Link>
             <SignedOut>
               <Link to="/sign-in" className="text-gray-700 hover:text-blue-600 transition font-medium">Sign In</Link>
             </SignedOut>
             <SignedIn>
               <Link to="/dashboard" className="text-gray-700 hover:text-blue-600 transition font-medium">Dashboard</Link>
               <UserButton afterSignOutUrl="/">
                 <UserButton.MenuItems>
                   <UserButton.Link
                     label="Company Profile"
                     labelIcon={<User size={16} />}
                     href="/profile"
                   />
                   <UserButton.Action
                     label="Manage Subscription"
                     labelIcon={<DollarSign size={16} />}
                     onClick={handleManageSubscription}
                   />
                 </UserButton.MenuItems>
               </UserButton>
             </SignedIn>
             <Link to="/estimator" className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2.5 rounded-lg hover:shadow-lg hover:scale-105 transition-all font-semibold">
               Try It Now
             </Link>
           </div>

            {/* Mobile menu button */}
            <button className="md:hidden text-gray-700" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle mobile menu">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-6 py-6 space-y-4">
              <a href="#features" className="block text-gray-700 hover:text-blue-600 font-medium">Features</a>
              <a href="#how-it-works" className="block text-gray-700 hover:text-blue-600 font-medium">How It Works</a>
              <a href="#faq" className="block text-gray-700 hover:text-blue-600 font-medium">FAQ</a>
              <Link to="/pricing" className="block text-gray-700 hover:text-blue-600 font-medium">Pricing</Link>
              <SignedIn>
                <Link to="/dashboard" className="block text-gray-700 hover:text-blue-600 font-medium">Dashboard</Link>
              </SignedIn>
              <SignedOut>
                <Link to="/sign-in" className="block text-gray-700 hover:text-blue-600 font-medium">Sign In</Link>
              </SignedOut>
              <Link to="/estimator" className="block w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:shadow-lg transition font-semibold text-center">
                Try It Now
              </Link>
            </div>
          </div>
        )}
      </nav>

    {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-blue-50"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Centered Headline */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900" style={{lineHeight: '1.2'}}>
              Spend Less Time Estimating,
              <span className="block bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent" style={{lineHeight: '1.3', paddingBottom: '4px'}}>
                More Time Building
              </span>
            </h1>
          </div>

          {/* Two Column Layout: Text Left, Sample Box Right */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Text Content */}
            <div className="space-y-6">
              {/* Subheadline */}
              <p className="text-xl text-gray-600 leading-relaxed">
                Describe the job by photo, voice, or text and get a detailed materials list with real pricing. Layer in labor, equipment, markup, and other fees, then review and send a polished PDF to your client.
              </p>

              {/* Credibility Line */}
              <p className="text-base text-gray-500 italic">
                Built with decades of hands-on construction industry experience—created by construction people, for construction people. We know estimating is tedious and time-consuming—so we made it faster and better.
              </p>

              {/* CTA Button */}
              <div>
                <Link to="/estimator" className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-lg hover:shadow-2xl hover:scale-105 transition-all font-semibold text-lg">
                  Start Your 7-Day Free Trial
                </Link>
              </div>

              {/* Pricing Transparency */}
              <p className="text-sm text-gray-500">
                Then just $29/month · Cancel anytime
              </p>

              {/* Key Benefits */}
              <div className="flex flex-wrap gap-6 pt-2">
                <div className="flex items-center space-x-2 text-gray-700">
                  <Wrench className="text-blue-600" size={20} />
                  <span className="font-medium">All major trade categories and more</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-700">
                  <Check className="text-blue-600" size={20} />
                  <span className="font-medium">7-Day Free Trial</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-700">
                  <Clock className="text-blue-600" size={20} />
                  <span className="font-medium">Estimates in a fraction of the time</span>
                </div>
              </div>
            </div>

            {/* Right Column - Sample Estimate Box */}
            <AnimatedEstimateCard />
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Everything You Need to Build Estimates
            </h2>
            <p className="text-xl text-gray-600">
              Simple, powerful tools to quickly create rough or exact contractor estimates
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 - Photo Upload */}
            <div className="group relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 text-white mb-6">
                <Camera size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Photo Upload</h3>
              <p className="text-gray-600 leading-relaxed">Upload project photos and let the software automatically identify materials and quantities.</p>
            </div>

            {/* Feature 2 - Voice Input */}
            <div className="group relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 text-white mb-6">
                <Mic size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Voice Input</h3>
              <p className="text-gray-600 leading-relaxed">Speak your project scope and have it turned into an organized materials list.</p>
            </div>

            {/* Feature 3 - Smart Pricing */}
            <div className="group relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 text-white mb-6">
                <Database size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">150+ Item Pricing Database</h3>
              <p className="text-gray-600 leading-relaxed">Access market-accurate pricing across major trade categories. One click to add prices—customize as needed.</p>
            </div>

            {/* Feature - Supplier Pricing */}
            <div className="group relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 text-white mb-6">
                <Users size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Your Supplier's Real Prices</h3>
              <p className="text-gray-600 leading-relaxed">Connect a supply house you buy from and their exact, current pricing flows straight into your estimates. Quote what you'll actually pay at the counter.</p>
            </div>

            {/* Feature 4 - Equipment Rentals */}
            <div className="group relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-gray-700 to-gray-800 text-white mb-6">
                <Wrench size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Equipment Rentals</h3>
              <p className="text-gray-600 leading-relaxed">Browse 150+ rental items with daily, weekly, and monthly rates built right into your estimates.</p>
            </div>

            {/* Feature 5 - Export Estimates */}
            <div className="group relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-gray-700 to-gray-800 text-white mb-6">
                <FileText size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Professional PDF Export</h3>
              <p className="text-gray-600 leading-relaxed">Download polished, professional estimates ready to share with clients.</p>
            </div>

            {/* Feature 6 - Save Time */}
            <div className="group relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-gray-700 to-gray-800 text-white mb-6">
                <Clock size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Save Hours Every Week</h3>
             <p className="text-gray-600 leading-relaxed">Drafts in minutes with the ability to review and customize. Generate quick rough estimates or create detailed breakdowns depending on your needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section id="who-its-for" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Built for Contractors Like You
            </h2>
            <p className="text-xl text-gray-600">
              Whether you need quick ballpark figures or detailed breakdowns, Estimate Assistant works the way you do.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {[
              'General Contractors',
              'Remodelers',
              'Carpenters',
              'Framers',
              'Roofers',
              'Electricians',
              'Plumbers',
              'Painters',
              'Deck Builders',
              'Handymen',
              'HVAC Technicians'
            ].map((trade) => (
              <div key={trade} className="flex items-center space-x-2 bg-white px-5 py-3 rounded-full shadow-sm border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all">
                <Users size={18} className="text-blue-600" />
                <span className="font-medium text-gray-700">{trade}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Three Simple Steps
            </h2>
            <p className="text-xl text-gray-600">Get started in minutes</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 relative">
           
            {/* Step 1 */}
            <div className="relative">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-6 shadow-lg relative z-10">1</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Describe Your Project</h3>
              <p className="text-gray-600 leading-relaxed">Type your job description, use voice input, or upload a photo. However you work best.</p>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-6 shadow-lg relative z-10">2</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Review & Customize</h3>
              <p className="text-gray-600 leading-relaxed">Get a full materials list with average market pricing or exact pricing from uploaded supplier lists. Measurements are converted to buyable quantities which you can adjust or add waste percentage.</p>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-6 shadow-lg relative z-10">3</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Export & Win Jobs</h3>
              <p className="text-gray-600 leading-relaxed">Download a professional PDF estimate and send it to your client.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Demos Section */}
      <section id="demos" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              See It in Action
            </h2>
            <p className="text-xl text-gray-600">
              Watch how fast you can build a complete estimate from scratch
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/GKeG3JHZUPo?si=u1YceLGXvmgLk8BZ"
                  title="Bathroom Remodel Estimate Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mt-4">Bathroom Remodel Estimate</h3>
              <p className="text-gray-600 text-sm">Full materials list and pricing in minutes</p>
            </div>

            <div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/80EB-wUVbIE"
                  title="Deck Build Estimate Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mt-4">New Deck Build Estimate</h3>
              <p className="text-gray-600 text-sm">Complete materials, labor, and PDF export</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about Estimate Assistant
            </p>
          </div>

          <div className="space-y-0">
            {[
              {
                q: "How does Estimate Assistant work?",
                a: "Describe your project using text, voice input, or by uploading a photo. Our AI analyzes your description and generates a complete materials list with quantities. From there, you can add market pricing with one click, adjust quantities, include labor and equipment costs, and export a professional PDF estimate — all in minutes."
              },
              {
                q: "What trades does it work for?",
                a: "Estimate Assistant works for a wide range of construction trades including general construction, bathroom and kitchen remodels, roofing, decks, plumbing, electrical, HVAC, painting, flooring, siding, and more. Whether you're a general contractor, remodeler, handyman, or specialty trade, the tool adapts to your project type."
              },
              {
                q: "How accurate are the estimates?",
                a: "Estimate Assistant generates material lists using AI trained on construction industry data, and pricing comes from market average databases across major trade categories. The estimates are designed to give you a strong starting point — you can then adjust quantities, swap in your own supplier pricing, and fine-tune everything before sending it to a client."
              },
              {
                            
                q: "Can I add my own pricing and materials?",
                a: "Yes. You can build a personal materials database with your own supplier pricing, upload catalog price lists, or manually adjust any price on an estimate. The tool checks your personal pricing first before falling back to market averages, so your estimates reflect what you actually pay."
              },
              {
                q: "Can I use a specific supplier's pricing in my estimates?",
                a: "Yes. If a supplier you buy from is on Estimate Assistant, ask them for their share code, enter it in your Profile under Connected Suppliers, and check 'Use supplier pricing' on any estimate. Your bids then use that supplier's real, current prices instead of market averages — so what you quote matches what you'll actually pay at their counter. Where a supplier hasn't listed a price, it falls back to market pricing or leaves it blank, never a guess."
              },
              {
                q: "How does a supplier upload their pricing catalog?",
                a: "Suppliers create a free account, upload their price list, and get a share code to hand out to the contractors they work with. There's no cost to the supplier and nothing for you to maintain — when they update their pricing, your connected estimates use the new numbers automatically."
              },
              {
                q: "Does it work on my phone?",
                a: "Yes. Estimate Assistant is fully mobile-friendly and works in any browser on your phone, tablet, or computer. No app download needed — just go to the site and start estimating. It's designed to work on the jobsite, in your truck, or at your desk."
              },
              {
                q: "What does the PDF estimate look like?",
                a: "You can generate two types of PDFs — a detailed contractor version with full cost breakdowns, and a clean client-facing version where you control what's visible. Add your company name, phone, email, license number, and client details. The result is a professional estimate you'd be proud to hand to any client."
              },
              {
                q: "How much does it cost?",
                a: "Estimate Assistant is $29 per month after a 7-day free trial. No contracts, cancel anytime. That's less than the cost of one hour of most contractors' time — and it saves you hours every week."
              },
              {
                q: "How is this different from other estimating software?",
                a: "Most estimating tools are built for large companies with big budgets and steep learning curves. Estimate Assistant is built specifically for small to medium-sized contractors who need fast, accurate estimates without the complexity. Describe your job in plain language and get a materials list in seconds — no training required."
              },
              {
                q: "Do I need to download anything?",
                a: "No. Estimate Assistant runs entirely in your web browser. Just sign up, log in, and start building estimates. Works on Chrome, Safari, Firefox, and Edge on any device."
              },
              {
                q: "Who built Estimate Assistant?",
                a: "Estimate Assistant was built by someone with decades of hands-on construction industry experience. Every feature was designed to solve a real problem that contractors face every day — not by a tech company guessing at what contractors need, but by someone who's been in the field."
              }
            ].map((item, index) => (
              <details key={index} className="group border-b border-gray-200">
                <summary className="flex items-center justify-between py-6 cursor-pointer list-none">
                  <span className="text-lg font-semibold text-gray-900 pr-8">{item.q}</span>
                  <span className="text-2xl text-gray-400 group-open:rotate-45 transition-transform flex-shrink-0">+</span>
                </summary>
                <p className="pb-6 text-gray-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Create Your First Estimate?
          </h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Try Estimate Assistant free for 7 days and see how easy professional estimating can be.
          </p>
          <Link to="/estimator" className="inline-block bg-white text-blue-600 px-8 py-4 rounded-xl hover:shadow-2xl hover:scale-105 transition-all font-bold text-lg">
            Get Started Now
          </Link>
          <p className="text-blue-200 mt-4 text-sm">
            7-day free trial · Then $29/month · Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img src="/Housecalclogo.png" alt="Estimate Assistant" className="h-10" />
                <span className="text-xl font-bold text-white">Estimate Assistant</span>
              </div>
              <p className="text-sm leading-relaxed">
                AI-powered construction estimating tool built for contractors. Create professional estimates in minutes, not hours.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="hover:text-blue-400 transition">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-blue-400 transition">How It Works</a></li>
                <li><Link to="/pricing" className="hover:text-blue-400 transition">Pricing</Link></li>
                <li><a href="#faq" className="hover:text-blue-400 transition">FAQ</a></li>
                <li><Link to="/estimator" className="hover:text-blue-400 transition">Try It Now</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/about" className="hover:text-blue-400 transition">About</Link></li>
                <li><Link to="/contact" className="hover:text-blue-400 transition">Contact</Link></li>
                <li><Link to="/terms" className="hover:text-blue-400 transition">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-blue-400 transition">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-sm text-center">
            © 2025 Estimate Assistant. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Homepage;